import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { theme } from '../../theme';

const BASE = '/api';
function authFetch(url, opts = {}) {
  const token = localStorage.getItem('arcus_token');
  return fetch(url, { ...opts, headers: { ...(opts.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
}

const DATE_LOCALES = { fr: 'fr-FR', en: 'en-GB', de: 'de-DE', sv: 'sv-SE' };
const fmt0 = v => v != null ? new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(v) + ' \u20ac' : '\u2014';
const fmtPct = v => v != null ? new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(v) + ' %' : '\u2014';

function monthYear(iso, lang) {
  if (!iso) return '\u2014';
  try {
    const s = new Date(iso).toLocaleDateString(DATE_LOCALES[lang] || 'fr-FR', { month: 'short', year: 'numeric' });
    return s.charAt(0).toUpperCase() + s.slice(1);
  } catch { return '\u2014'; }
}
function fmtDate(iso, lang) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '\u2014';
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.toLocaleDateString(DATE_LOCALES[lang] || 'fr-FR', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

// Couleurs des badges de statut (workflow GF complet)
const STATUS_STYLE = {
  demande_ouverture: { bg: '#FEF3E2', color: '#9A5A00' },
  demande_pieces:    { bg: '#FEF9C3', color: '#854D0E' },
  en_cours_etude:    { bg: '#E6F1FB', color: '#185FA5' },
  devis:             { bg: '#EDE9FE', color: '#6D28D9' },
  souscrit:          { bg: '#D1FAE5', color: '#065F46' },
  suspendu:          { bg: '#FEF3E2', color: '#9A5A00' },
  resilie:           { bg: '#FEE2E2', color: '#991B1B' },
  renouvelee:        { bg: '#F1F1EE', color: '#5F5E5A' },
  sans_objet:        { bg: '#F1F1EE', color: '#5F5E5A' },
  decline:           { bg: '#FEE2E2', color: '#991B1B' },
};

export default function PortefeuilleGF() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [polices, setPolices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const [filters, setFilters] = useState({});      // { colKey: Set(valeurs affichées) }
  const [openFilter, setOpenFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState('');
  const [sort, setSort] = useState({ key: 'moisKey', dir: 'desc' });
  const popRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await authFetch(`${BASE}/polices`);
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || 'Erreur');
        setPolices((Array.isArray(d) ? d : []).filter(p => p.produit === 'gf'));
        setError('');
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, []);

  // Fermer le filtre au clic extérieur
  useEffect(() => {
    const h = e => { if (popRef.current && !popRef.current.contains(e.target)) { setOpenFilter(null); setFilterSearch(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const statusLabel = useCallback(s => {
    const v = t('status.' + s);
    if (v && !String(v).startsWith('status.')) return v;
    if (s === 'decline') return t('polices.dossier_decline_status');
    return s;
  }, [t]);

  // ── Dérivation des lignes ────────────────────────────────────────────
  const rows = useMemo(() => polices.map(p => {
    const pg = p.payment_graph_gf || {};
    const cot = pg.cotation;
    const isFR = (p.marche_pays || '') === 'France';
    const peak = (cot && Array.isArray(cot.periods) && cot.periods.length)
      ? Math.max(...cot.periods.map(x => parseFloat(x.exposition) || 0)) : null;
    const taux = (pg.taux != null && pg.taux !== '') ? parseFloat(pg.taux) : null;
    return {
      id: p.id,
      moisKey: String(p.created_at || '').slice(0, 7),
      mois: monthYear(p.created_at, lang),
      statut: p.statut,
      statutLabel: statusLabel(p.statut),
      nom: p.client_nom || '\u2014',
      ref: p.n_police || p.n_devis || '',
      pays: p.marche_pays || '\u2014',
      courtier: p.courtier_nom || t('gf.pf_direct'),
      datePrevKey: p.date_effet_prev || '',
      date_prev: fmtDate(p.date_effet_prev, lang),
      exposure: isFR ? (peak != null ? peak : 200000) : null,
      taux: isFR ? (taux != null ? taux : 1) : null,
      premium: isFR ? (cot && cot.total_ttc != null ? cot.total_ttc : 2000) : null,
    };
  }), [polices, lang, t, statusLabel]);

  // ── Colonnes (clé de tri, valeur affichée / de filtre) ───────────────
  const COLS = useMemo(() => [
    { key: 'mois',      w: '9%',  label: t('gf.pf_col_month'),     sortKey: r => r.moisKey,            filterVal: r => r.mois,        render: r => r.mois },
    { key: 'statut',    w: '15%', label: t('gf.pf_col_status'),    sortKey: r => r.statutLabel,        filterVal: r => r.statutLabel,
      render: r => { const st = STATUS_STYLE[r.statut] || { bg: '#F1F1EE', color: '#5F5E5A' };
        return <span style={{ fontSize: 10.5, fontWeight: 700, background: st.bg, color: st.color, borderRadius: 99, padding: '3px 9px', display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.statutLabel}</span>; } },
    { key: 'nom',       w: '18%', label: t('gf.pf_col_name'),      sortKey: r => r.nom.toLowerCase(),  filterVal: r => r.nom,
      render: r => (<div style={{ overflow: 'hidden' }}><div style={{ fontWeight: 700, color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.nom}</div>{r.ref && <div style={{ fontSize: 10.5, color: theme.textTertiary, fontFamily: 'monospace' }}>{r.ref}</div>}</div>) },
    { key: 'pays',      w: '9%',  label: t('gf.pf_col_country'),   sortKey: r => r.pays,               filterVal: r => r.pays,        render: r => r.pays },
    { key: 'courtier',  w: '13%', label: t('gf.pf_col_broker'),    sortKey: r => r.courtier.toLowerCase(), filterVal: r => r.courtier, render: r => r.courtier },
    { key: 'date_prev', w: '11%', label: t('gf.pf_col_prev_date'), sortKey: r => r.datePrevKey,        filterVal: r => r.date_prev,   render: r => r.date_prev },
    { key: 'exposure',  w: '9%',  label: 'Exposure',               sortKey: r => r.exposure ?? -1,     filterVal: r => fmt0(r.exposure),  render: r => <span style={{ fontWeight: 600 }}>{fmt0(r.exposure)}</span>, num: true },
    { key: 'taux',      w: '7%',  label: 'Premium rate',           sortKey: r => r.taux ?? -1,         filterVal: r => fmtPct(r.taux),    render: r => fmtPct(r.taux), num: true },
    { key: 'premium',   w: '9%',  label: 'Premium',                sortKey: r => r.premium ?? -1,      filterVal: r => fmt0(r.premium),   render: r => <span style={{ fontWeight: 700, color: '#0F6E56' }}>{fmt0(r.premium)}</span>, num: true },
  ], [t, lang]);

  // ── Filtrage (recherche globale + filtres colonnes) ──────────────────
  const filtered = useMemo(() => {
    let out = rows;
    const q = search.trim().toLowerCase();
    if (q) out = out.filter(r => r.nom.toLowerCase().includes(q) || r.ref.toLowerCase().includes(q));
    for (const col of COLS) {
      const sel = filters[col.key];
      if (sel && sel.size) out = out.filter(r => sel.has(col.filterVal(r)));
    }
    return out;
  }, [rows, search, filters, COLS]);

  // ── Tri ──────────────────────────────────────────────────────────────
  const sorted = useMemo(() => {
    const col = COLS.find(c => c.key === sort.key) || COLS[0];
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = col.sortKey(a), vb = col.sortKey(b);
      if (va < vb) return -dir; if (va > vb) return dir; return 0;
    });
  }, [filtered, sort, COLS]);

  // Valeurs distinctes d'une colonne (pour le menu de filtre)
  const distinctValues = col => {
    const set = new Set(rows.map(r => col.filterVal(r)));
    const arr = [...set];
    if (col.num) arr.sort((a, b) => (parseFloat(String(a).replace(/[^\d.,-]/g, '').replace(',', '.')) || 0) - (parseFloat(String(b).replace(/[^\d.,-]/g, '').replace(',', '.')) || 0));
    else arr.sort((a, b) => String(a).localeCompare(String(b), 'fr'));
    return arr;
  };

  const toggleFilterValue = (colKey, val) => setFilters(f => {
    const cur = new Set(f[colKey] || []);
    if (cur.has(val)) cur.delete(val); else cur.add(val);
    return { ...f, [colKey]: cur.size ? cur : null };
  });

  const sortIndicator = key => sort.key === key ? (sort.dir === 'asc' ? ' \u25b2' : ' \u25bc') : '';

  return (
    <div style={{ width: '100%', maxWidth: 1600, margin: '0 auto' }}>
      {/* Titre */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', borderBottom: `2px solid ${theme.text}`, paddingBottom: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={theme.orange} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ alignSelf: 'center' }}>
            <path d="M3 21l18 0M3 10l18 0M5 6l7 -3l7 3M4 10l0 11M20 10l0 11M8 14l0 3M12 14l0 3M16 14l0 3" />
          </svg>
          <span style={{ fontSize: 22, fontWeight: 800, color: theme.text }}>{t('gf.pf_title')}</span>
          <span style={{ fontSize: 12.5, color: theme.textTertiary }}>{sorted.length} {t('gf.pf_contracts')}</span>
        </div>
        {(Object.values(filters).some(s => s && s.size) || search) && (
          <button onClick={() => { setFilters({}); setSearch(''); }}
            style={{ fontSize: 12, padding: '5px 12px', borderRadius: 99, border: `1px solid ${theme.border}`, background: '#fff', color: theme.textSecondary, cursor: 'pointer', fontWeight: 600 }}>
            ✕ {t('gf.pf_filter_clear_all')}
          </button>
        )}
      </div>

      {/* Recherche globale */}
      <div style={{ marginBottom: 14 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('gf.pf_search')}
          style={{ width: 340, maxWidth: '100%', border: `1px solid ${theme.border}`, borderRadius: 8, padding: '9px 12px', fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' }} />
      </div>

      {error && <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#991B1B' }}>⚠ {error}</div>}
      {loading && <div style={{ padding: 40, textAlign: 'center', color: theme.textTertiary }}>…</div>}

      {!loading && (
      <div style={{ background: '#fff', border: `1px solid ${theme.borderStrong}`, borderRadius: theme.radiusLg, overflow: 'visible' }}>
        <div>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr style={{ background: theme.surfaceAlt, borderBottom: `1px solid ${theme.borderStrong}` }}>
                {COLS.map(col => {
                  const active = filters[col.key] && filters[col.key].size;
                  return (
                    <th key={col.key} style={{ width: col.w, padding: '9px 7px', fontSize: 10.5, fontWeight: 700, color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: '.02em', textAlign: col.num ? 'right' : 'left', position: 'relative', verticalAlign: 'bottom' }}>
                      <span onClick={() => setSort(s => ({ key: col.key, dir: s.key === col.key && s.dir === 'asc' ? 'desc' : 'asc' }))}
                        style={{ cursor: 'pointer', userSelect: 'none' }}>
                        {col.label}{sortIndicator(col.key)}
                      </span>
                      <button onClick={() => { setOpenFilter(o => o === col.key ? null : col.key); setFilterSearch(''); }}
                        title={t('gf.pf_filter')}
                        style={{ marginLeft: 3, border: 'none', background: 'none', cursor: 'pointer', padding: 1, verticalAlign: 'middle' }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill={active ? theme.orange : 'none'} stroke={active ? theme.orange : theme.textTertiary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 4h16v2.172a2 2 0 0 1 -.586 1.414l-4.414 4.414v7l-6 2v-8.5l-4.48 -4.928a2 2 0 0 1 -.52 -1.345z" />
                        </svg>
                      </button>

                      {/* Menu de filtre (façon Excel) */}
                      {openFilter === col.key && (
                        <div ref={popRef} style={{ position: 'absolute', top: '100%', [col.num ? 'right' : 'left']: 0, zIndex: 500, background: '#fff', border: `1px solid ${theme.borderStrong}`, borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.14)', width: 230, padding: 10, textTransform: 'none', fontWeight: 400 }}>
                          <input autoFocus value={filterSearch} onChange={e => setFilterSearch(e.target.value)} placeholder={t('gf.pf_filter_search')}
                            style={{ width: '100%', border: `1px solid ${theme.border}`, borderRadius: 6, padding: '5px 8px', fontSize: 12, boxSizing: 'border-box', marginBottom: 8, fontFamily: 'inherit' }} />
                          <div style={{ display: 'flex', gap: 10, fontSize: 11.5, marginBottom: 6 }}>
                            <span onClick={() => setFilters(f => ({ ...f, [col.key]: null }))} style={{ color: theme.orange, cursor: 'pointer', fontWeight: 700 }}>{t('gf.pf_filter_all')}</span>
                            <span onClick={() => setFilters(f => ({ ...f, [col.key]: new Set(['\u2205']) }))} style={{ color: theme.textTertiary, cursor: 'pointer', fontWeight: 700 }}>{t('gf.pf_filter_none')}</span>
                          </div>
                          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                            {distinctValues(col).filter(v => !filterSearch || String(v).toLowerCase().includes(filterSearch.toLowerCase())).map(v => {
                              const sel = filters[col.key];
                              const checked = !sel || sel.has(v);
                              return (
                                <label key={String(v)} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: theme.text, padding: '3px 2px', cursor: 'pointer' }}>
                                  <input type="checkbox" checked={checked}
                                    onChange={() => {
                                      if (!sel) { // tout était sélectionné → ne garder que les autres
                                        const all = new Set(distinctValues(col)); all.delete(v);
                                        setFilters(f => ({ ...f, [col.key]: all }));
                                      } else toggleFilterValue(col.key, v);
                                    }}
                                    style={{ accentColor: theme.orange, flexShrink: 0 }} />
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(v)}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => (
                <tr key={r.id} onClick={() => navigate(`/gf/${r.id}`)}
                  style={{ borderTop: i ? `1px solid ${theme.border}` : 'none', cursor: 'pointer', background: i % 2 ? '#FCFCFB' : '#fff' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#FFF7F2'}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 ? '#FCFCFB' : '#fff'}>
                  {COLS.map(col => (
                    <td key={col.key} style={{ padding: '10px 7px', fontSize: 12.5, color: theme.textSecondary, textAlign: col.num ? 'right' : 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {col.render(r)}
                    </td>
                  ))}
                </tr>
              ))}
              {!sorted.length && (
                <tr><td colSpan={COLS.length} style={{ padding: 32, textAlign: 'center', color: theme.textTertiary, fontSize: 13 }}>{t('gf.pf_no_rows')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
}
